import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASONS = [
    { key: "harmful", label: "felt harmful or unsafe" },
    { key: "inappropriate", label: "inappropriate content" },
    { key: "crisis_mishandled", label: "mishandled a crisis moment" },
    { key: "inaccurate", label: "misleading or inaccurate" },
    { key: "other", label: "something else" },
];

export default function ReportDialog({ messageId, onClose }) {
    const [reason, setReason] = useState(null);
    const [details, setDetails] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        if (!reason) return;
        setSending(true);
        try {
            await api.post("/reports", { message_id: messageId, reason, details: details || null });
            toast.success("Thank you — we review every report within 24 hours.");
            onClose();
        } catch {
            toast.error("Couldn't send the report. Please try again.");
            setSending(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={onClose}
                data-testid="report-dialog"
            >
                <motion.div
                    initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                    className="bg-bone rounded-3xl p-6 md:p-8 max-w-md w-full shadow-soft border border-clayHover"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-start justify-between mb-4">
                        <h3 className="font-heading text-xl text-ink flex items-center gap-2">
                            <Flag className="w-4 h-4 text-terracotta" /> report this reply
                        </h3>
                        <button onClick={onClose} data-testid="report-close" className="text-inkSoft hover:text-ink" aria-label="Close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <p className="text-sm font-body text-inkSoft mb-4 leading-relaxed">
                        If a reply from pax felt wrong, tell us. A human reviews every report.
                    </p>
                    <div className="space-y-2 mb-4">
                        {REASONS.map(r => (
                            <button
                                key={r.key}
                                data-testid={`report-reason-${r.key}`}
                                onClick={() => setReason(r.key)}
                                className={`w-full text-left px-4 py-2.5 rounded-xl font-body text-sm transition-colors ${
                                    reason === r.key ? "bg-terracotta/15 text-terracotta border border-terracotta/40" : "bg-clay/50 text-ink border border-transparent hover:bg-clay"
                                }`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <textarea
                        data-testid="report-details"
                        value={details}
                        onChange={e => setDetails(e.target.value)}
                        rows={2}
                        placeholder="anything else we should know? (optional)"
                        className="w-full bg-clay/40 border border-clayHover rounded-xl px-4 py-3 font-body text-sm text-ink outline-none resize-none mb-4"
                    />
                    <button
                        data-testid="report-submit"
                        onClick={submit}
                        disabled={!reason || sending}
                        className="w-full py-3 rounded-full bg-terracotta text-bone font-body text-sm disabled:opacity-40 hover:bg-terracottaHover transition-colors"
                    >
                        {sending ? "sending…" : "send report"}
                    </button>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
